/**
 * ProfileDropdown — user menu in the topbar.
 *
 * Contains, in order:
 *   1. Header with user name / email / role
 *   2. Theme switch    (light / dark, segmented control)
 *   3. Language switch (English / العربية, segmented control)
 *   4. Profile Settings link  → /profile-settings
 *   5. Log out button         → confirmation + logout()
 *
 * Click trigger to open; click outside or Esc to close.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineUserCircle,
  HiOutlineChevronDown,
  HiOutlineArrowRightOnRectangle,
  HiOutlineSun,
  HiOutlineMoon,
} from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useDirection } from '../../contexts/DirectionContext'

export default function ProfileDropdown() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const { lang, setLang } = useDirection()

  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  // Click outside or Esc closes
  useEffect(() => {
    if (!open) return undefined
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleLogout = () => {
    setOpen(false)
    if (window.confirm(t('profile.logoutConfirm'))) {
      logout()
    }
  }

  const initial = (user?.name || user?.email || 'U').charAt(0).toUpperCase()

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={t('profile.menuLabel')}
        aria-expanded={open}
        className="flex items-center gap-2 ps-1 pe-2 py-1 rounded-full hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-brand/30"
      >
        <div className="w-8 h-8 bg-brand-soft rounded-full flex items-center justify-center">
          <span className="text-brand font-semibold text-sm">{initial}</span>
        </div>
        <HiOutlineChevronDown className={`w-4 h-4 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 mt-2 w-72 bg-elevated border border-border rounded-xl shadow-token-lg py-1 z-50"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-soft rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-brand font-semibold">{initial}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-primary truncate">{user?.name || t('profile.user')}</p>
                {user?.email && (
                  <p className="text-xs text-ink-muted truncate">{user.email}</p>
                )}
                {user?.role && (
                  <p className="text-[10px] uppercase tracking-wider text-ink-muted mt-0.5">{user.role}</p>
                )}
              </div>
            </div>
          </div>

          {/* Theme switch */}
          <div className="px-3 pt-3 pb-1">
            <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider px-1 pb-1.5">{t('theme.label')}</p>
            <Segmented
              value={resolvedTheme}
              onChange={setTheme}
              options={[
                { value: 'light', icon: HiOutlineSun,  label: t('theme.light') },
                { value: 'dark',  icon: HiOutlineMoon, label: t('theme.dark') },
              ]}
            />
          </div>

          {/* Language switch */}
          <div className="px-3 pt-3 pb-2 border-b border-border-subtle">
            <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider px-1 pb-1.5">{t('language.label')}</p>
            <Segmented
              value={lang}
              onChange={setLang}
              options={[
                { value: 'en', label: 'EN · English' },
                { value: 'ar', label: 'AR · العربية' },
              ]}
            />
          </div>

          {/* Actions */}
          <div className="py-1">
            <MenuItem
              icon={HiOutlineUserCircle}
              label={t('profile.profileSettings')}
              onClick={() => { setOpen(false); navigate('/profile-settings') }}
            />
          </div>

          <div className="border-t border-border-subtle py-1">
            <MenuItem
              icon={HiOutlineArrowRightOnRectangle}
              label={t('profile.logout')}
              tone="danger"
              onClick={handleLogout}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function MenuItem({ icon: Icon, label, onClick, tone = 'default' }) {
  const toneClasses = tone === 'danger'
    ? 'text-danger hover:bg-danger-soft'
    : 'text-ink-primary hover:bg-muted'
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${toneClasses}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="flex p-1 rounded-lg bg-muted">
      {options.map(opt => {
        const Icon = opt.icon
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
              active
                ? 'bg-elevated text-ink-primary shadow-token-sm'
                : 'text-ink-muted hover:text-ink-secondary'
            }`}
            aria-pressed={active}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
